'use client'

import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import WaitlistModal from '../WaitlistModal'
import AppStoreLink from '../AppStoreLink'

const ALL_PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  x: ((i * 137.508) % 100),
  y: ((i * 61.803) % 100),
  size: 4 + (i % 7) * 3,
  delay: (i % 5) * 0.6,
  duration: 18 + (i % 8) * 4,
  shape: i % 4,
  opacity: 0.1 + (i % 5) * 0.05,
}))

function Particle({ particle }: { particle: typeof ALL_PARTICLES[0] }) {
  const clipPaths: Record<number, string | undefined> = {
    1: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
    3: 'polygon(30% 0%, 100% 20%, 70% 100%, 0% 80%)',
  }

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        width: particle.size,
        height: particle.shape === 2 ? particle.size * 2.5 : particle.size,
      }}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{
        opacity: [0, particle.opacity, particle.opacity, 0],
        scale: [0.5, 1, 1, 0.5],
        x: [0, 25, -15, 0],
        y: [0, -30, -60, -100],
        rotate: [0, 45, 90, 135],
      }}
      transition={{
        duration: particle.duration,
        delay: particle.delay,
        repeat: Infinity,
        ease: 'linear',
      }}
    >
      <div
        className={`w-full h-full ${particle.shape === 0 || particle.shape === 2 ? 'rounded-full' : ''} bg-gradient-to-br from-goldLight/40 to-goldLight/10`}
        style={clipPaths[particle.shape] ? { clipPath: clipPaths[particle.shape] } : undefined}
      />
    </motion.div>
  )
}

// The actual App Store marketing cards (1320x2868 exports) — each one is a
// finished composition: gold-framed device, brand background, caption. Shown
// whole, never cropped, and shuffled on a slow timer.
const STORE_CARDS = [
  { src: '/appstore/01-hero.webp', alt: "App Store card: You'd never guess what's in it. The Today screen with exposure gauge." },
  { src: '/appstore/02-device-top.webp', alt: 'App Store card: Every score shows its receipts. Scan log with scored products.' },
  { src: '/appstore/04-device-top.webp', alt: 'App Store card: Every room, mapped. Household exposure map.' },
  { src: '/appstore/05-device-bottom.webp', alt: 'App Store card: Make it a habit. MicroQuests library.' },
  { src: '/appstore/06-device-top.webp', alt: 'App Store card: Watch your exposure drop. Reports with weekly trends.' },
  { src: '/appstore/07-two-devices.webp', alt: 'App Store card: Collect your wins. The Plastic Passport.' },
  { src: '/appstore/03-device-bottom.webp', alt: 'App Store card: Swap up, plastic down. Explore tab with tested lists.' },
  { src: '/appstore/08-device-top.webp', alt: 'App Store card: Start free. No account. First scan screen.' },
] as const

const CARD_INTERVAL_MS = 4200

// How each card folds away when it's replaced. A card is hinged on one of its
// own edges (or a corner) and swings back through ~100° like a closing door,
// revealing the next card underneath.
//
// Two things worth knowing before editing this:
//
//  1. The hinge is always ON the card — never a translate that carries it out
//     of frame. The bezel is overflow-hidden, so a card that actually flew off
//     to the right would be sliced in half at the rounded edge. Rotating about
//     an edge foreshortens the card *inward*, so the whole motion stays inside
//     the frame and still reads as "it left to the right".
//  2. In AnimatePresence, an exiting child keeps the props it had while it was
//     current. So a card owns the direction it leaves by — FOLDS is indexed by
//     the card's own position, not by the transition counter. 6 folds against 8
//     cards means the pairing shifts every lap (LCM 24), so the deck never
//     settles into "this card always folds right".
//
// CSS sign conventions, since they are easy to get backwards: +rotateY swings
// the LEFT edge toward the viewer; +rotateX tips the TOP edge away from it.
const FOLDS = [
  // hinge right edge — folds away to the right
  { origin: '100% 50%', exit: { rotateY: -102, x: '7%' } },
  // hinge bottom edge — top edge falls away, like a laptop lid closing
  { origin: '50% 100%', exit: { rotateX: 102, y: '6%' } },
  // hinge left edge — folds away to the left
  { origin: '0% 50%', exit: { rotateY: 102, x: '-7%' } },
  // hinge top edge — bottom edge swings up and back
  { origin: '50% 0%', exit: { rotateX: -102, y: '-6%' } },
  // hinge bottom-right corner — diagonal peel
  { origin: '100% 100%', exit: { rotateY: -84, rotateX: 46, rotateZ: 5 } },
  // hinge top-left corner — diagonal peel the other way
  { origin: '0% 0%', exit: { rotateY: 84, rotateX: -46, rotateZ: -5 } },
] as const

export default function ParticleHero() {
  const prefersReducedMotion = useReducedMotion()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [card, setCard] = useState(0)
  const [deckHovered, setDeckHovered] = useState(false)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Shuffle the App Store card deck. Paused while hovered (the deck is a
  // link; swapping a target mid-hover is hostile) and under reduced motion.
  useEffect(() => {
    if (prefersReducedMotion || deckHovered) return
    const t = setInterval(() => setCard((i) => (i + 1) % STORE_CARDS.length), CARD_INTERVAL_MS)
    return () => clearInterval(t)
  }, [prefersReducedMotion, deckHovered])

  // Mobile particle budget is tight — every motion div costs INP. Drop to 6
  // on mobile; the cursor-spring no-ops on touch anyway. Desktop keeps 22.
  const PARTICLES = isMobile ? ALL_PARTICLES.slice(0, 6) : ALL_PARTICLES

  const springX = useSpring(useTransform(mouseX, [-0.5, 0.5], [-12, 12]), { stiffness: 50, damping: 20 })
  const springY = useSpring(useTransform(mouseY, [-0.5, 0.5], [-12, 12]), { stiffness: 50, damping: 20 })

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5)
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5)
  }, [mouseX, mouseY])

  const activeCard = STORE_CARDS[card]
  const activeFold = FOLDS[card % FOLDS.length]

  return (
    <>
      {/* Deep-forest hero — the premium dark panel of the dark/light
          alternation. -mt-16/pt-16 pulls the section up under the fixed
          transparent nav so the green field runs edge-to-edge behind it. */}
      <section
        className="relative -mt-16 pt-16 bg-deepForest overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {/* Particle field: gold-dust motes on the green field */}
        <motion.div
          className="absolute inset-0"
          style={{ x: springX, y: springY }}
        >
          {PARTICLES.map((p) => (
            <Particle key={p.id} particle={p} />
          ))}
        </motion.div>

        {/* Soft radial glow behind the card deck */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 70% 45%, rgba(229,195,106,0.10) 0%, rgba(229,195,106,0) 70%)',
          }}
        />

        <div className="relative z-10 frame pt-10 sm:pt-14 md:pt-20 pb-14 sm:pb-section">
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-8 items-center">
            {/* ── Copy column ─────────────────────────────────────────── */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-5 enter">
                <Image
                  src="/icon1.png"
                  alt=""
                  width={44}
                  height={44}
                  priority
                  className="rounded-xl w-10 h-10 sm:w-11 sm:h-11 shadow-lg shadow-black/30"
                />
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-goldLight">
                  Scan · Score · Swap
                </p>
              </div>

              {/* Deliberately NOT animated. This is the Largest Contentful
                  Paint element, and a browser cannot register LCP until the
                  element is painted at non-zero opacity — so animating it in
                  pushes the metric out by however long the entrance runs. It
                  previously started at `opacity: 0` behind a 0.2s delay and an
                  0.8s duration, and additionally animated `filter: blur(8px)`,
                  which repaints the largest text on the page every frame
                  instead of staying on the compositor. It now paints once, in
                  its final state, as fast as the font allows. */}
              <h1 className="font-display text-[clamp(2.1rem,6.5vw,3.9rem)] font-bold tracking-tight text-cream mb-4 md:mb-5 leading-[1.06]">
                The <span className="text-goldLight">microplastics app</span> for everyday food, water &amp; products
              </h1>

              <p className="text-sm sm:text-base md:text-lg text-cream/75 mb-7 md:mb-8 max-w-xl mx-auto md:mx-0 leading-relaxed enter enter-1">
                Scan barcodes and packaging photos to get a{' '}
                <strong className="text-cream">0–100 microplastic risk score</strong> backed by
                published research. Free to start on iOS.
              </p>

              {/* Single dominant CTA (App Store) + quiet Android text link. */}
              {/* The App Store button, the Android link and the trust strip
                  below are the only conversion actions on the page. As Framer
                  Motion elements parked at `opacity: 0` they rendered NOT AT
                  ALL when the JS bundle was blocked, slow or broken — verified
                  by loading the live page with JavaScript disabled. They are
                  plain elements now, animated by a CSS keyframe that runs on
                  stylesheet parse, needs no hydration and stays off the main
                  thread. The old sequence also delayed this row to ~1.5s; it
                  now starts at 80ms. */}
              <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 justify-center md:justify-start items-stretch xs:items-center w-full max-w-md mx-auto md:mx-0 xs:max-w-none enter enter-2">
                <AppStoreLink
                  source="home_hero"
                  ctaPosition="above_fold"
                  trackingParams={{ page_type: 'home' }}
                  className="inline-flex items-center justify-center w-full xs:w-auto bg-cream text-ink px-6 sm:px-7 py-3.5 rounded-control hover:bg-white transition-colors duration-200 shadow-lg shadow-black/25 text-base font-semibold min-h-[48px] pressable"
                >
                  <svg className="w-5 h-5 mr-2.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Download on the App Store
                </AppStoreLink>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center justify-center w-full xs:w-auto px-4 py-3.5 rounded-control text-cream/70 hover:text-cream underline underline-offset-4 decoration-cream/30 hover:decoration-cream transition-colors duration-200 text-sm min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight pressable"
                >
                  On Android? Join the waitlist
                </button>
              </div>

              {/* Trust strip: three short, factual claims under the CTA. */}
              <ul
                className="mt-5 md:mt-6 flex flex-wrap items-center justify-center md:justify-start gap-x-2 gap-y-1 text-xs sm:text-sm text-cream/60 enter enter-3"
                aria-label="Key facts about the MicroPlastics app"
              >
                <li><span className="font-semibold text-goldLight">5.0★ on the App Store</span></li>
                <li aria-hidden="true" className="text-cream/30">·</li>
                <li><span className="font-semibold text-cream">3 free scans</span></li>
                <li aria-hidden="true" className="text-cream/30">·</li>
                <li><span className="font-semibold text-cream">Research-backed risk score</span></li>
              </ul>
            </div>

            {/* ── App Store card deck: the real store marketing cards,
                   shown whole and shuffled on a slow timer. The deck links
                   to the App Store. ─────────────────────────────────────── */}
            <div
              className="relative flex justify-center md:justify-end enter enter-2"
              onMouseEnter={() => setDeckHovered(true)}
              onMouseLeave={() => setDeckHovered(false)}
            >
              <AppStoreLink
                source="home_hero_screenshot"
                ctaPosition="above_fold"
                trackingParams={{ page_type: 'home', card: activeCard.src }}
                aria-label="Download MicroPlastics on the App Store"
                className="block group"
              >
                {/* perspective must sit on the transformed card's PARENT, so it
                    lives here rather than on the card. See FOLDS above for how
                    each card leaves. */}
                <div
                  className="relative w-[260px] sm:w-[300px] md:w-[320px] aspect-[1320/2868] rounded-[1.9rem] shadow-[0_30px_80px_rgba(0,0,0,0.45)] overflow-hidden ring-1 ring-white/10 transition-transform duration-300 ease-out [@media(hover:hover)and(pointer:fine)]:group-hoverable:scale-[1.015]"
                  style={{ perspective: 1500 }}
                >
                  <AnimatePresence initial={false}>
                    <motion.div
                      key={activeCard.src}
                      className="absolute inset-0"
                      style={{
                        transformOrigin: activeFold.origin,
                        transformStyle: 'preserve-3d',
                        backfaceVisibility: 'hidden',
                      }}
                      // The entrance stays axis-neutral on purpose: the incoming
                      // card is the one being *revealed* under the fold, so it
                      // should sit still and settle, not perform its own spin.
                      // All the choreography belongs to the card leaving.
                      initial={
                        prefersReducedMotion
                          ? { opacity: 0 }
                          : { opacity: 0, scale: 0.94, zIndex: 1 }
                      }
                      animate={
                        prefersReducedMotion
                          ? { opacity: 1 }
                          : { opacity: 1, scale: 1, zIndex: 1 }
                      }
                      exit={
                        prefersReducedMotion
                          ? { opacity: 0 }
                          : { ...activeFold.exit, opacity: 0, zIndex: 2 }
                      }
                      transition={{
                        duration: prefersReducedMotion ? 0 : 0.9,
                        ease: [0.76, 0, 0.24, 1],
                        // Fade out ahead of the rotation so the card is gone
                        // before it reaches the ugly edge-on sliver at ~90°.
                        opacity: { duration: prefersReducedMotion ? 0 : 0.55, ease: 'easeIn' },
                      }}
                    >
                      <Image
                        src={activeCard.src}
                        alt={activeCard.alt}
                        width={720}
                        height={1565}
                        priority={card === 0}
                        sizes="(max-width: 640px) 260px, 320px"
                        className="w-full h-auto"
                      />
                      {/* Specular sheen that rakes across the card as it folds —
                          sells the 3D. Compositor-only, so it costs nothing. */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background:
                            'linear-gradient(105deg, rgba(255,255,255,0) 42%, rgba(255,255,255,0.16) 52%, rgba(255,255,255,0) 62%)',
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </AppStoreLink>

              {/* Deck position dots: decorative rhythm cue, not navigation. */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:right-[calc(50%-1rem)] md:translate-x-1/2 flex gap-1.5" aria-hidden="true">
                {STORE_CARDS.map((c, i) => (
                  <span
                    key={c.src}
                    className={`h-1 rounded-full transition-[width,background-color] duration-300 ease-out ${
                      i === card ? 'w-4 bg-goldLight/90' : 'w-1 bg-cream/25'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Preload the whole deck so shuffles never flash. `loading="eager"`
            is required: lazy images inside display:none never load. */}
        <div className="hidden" aria-hidden="true">
          {STORE_CARDS.map((c) => (
            <Image key={c.src} src={c.src} alt="" width={720} height={1565} loading="eager" />
          ))}
        </div>
      </section>

      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} source="home_hero" />
    </>
  )
}
